
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, FileMusic, BarChart3, HardDrive } from 'lucide-react';

export default function SystemStats() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalExtractions: 0,
    totalApiCalls: 0,
    totalStorageUsed: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch user count
      const { count: userCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Fetch extraction count
      const { count: extractionCount } = await supabase
        .from('video_extractions')
        .select('*', { count: 'exact', head: true });

      // Fetch total API calls and storage
      const { data: aggregateData } = await supabase
        .from('profiles')
        .select('api_calls_used, monthly_space_used');

      const totalApiCalls = aggregateData?.reduce((sum, profile) => sum + profile.api_calls_used, 0) || 0;
      const totalStorageUsed = aggregateData?.reduce((sum, profile) => sum + (profile.monthly_space_used || 0), 0) || 0;

      setStats({
        totalUsers: userCount || 0,
        totalExtractions: extractionCount || 0,
        totalApiCalls,
        totalStorageUsed,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatStorageSize = (bytes: number) => {
    const mb = bytes / 1024 / 1024;
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalUsers}</div>
          <p className="text-xs text-muted-foreground">
            Registered users
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Extractions</CardTitle>
          <FileMusic className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalExtractions}</div>
          <p className="text-xs text-muted-foreground">
            Total video extractions
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">API Calls</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalApiCalls}</div>
          <p className="text-xs text-muted-foreground">
            Total API usage
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
          <HardDrive className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatStorageSize(stats.totalStorageUsed)}</div>
          <p className="text-xs text-muted-foreground">
            Total storage consumed
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
